
import * as moment from "moment";

export class TypeParser {
    private static _emptyStrings : string[]  = ['', '0', 'false', 'null'];
    private static _emptyValues  : unknown[] = [0, false, NaN, null, undefined];
    private static _trueOptions  : string[]  = ['1', 'true', 'yes', 'on'];
    private static _falseOptions : string[]  = ['0', 'false', 'no', 'off'];

    public static isEmpty( value : unknown ): boolean {
        if( typeof value === 'string' ) {
            return TypeParser._emptyStrings.includes(value.toLowerCase());
        }
        return TypeParser._emptyValues.includes(value);
    }
    
    public static isObject(v: unknown): v is Record<string, unknown> {
        return typeof v === 'object' && v !== null;
    }

    public static parseDate(raw: string): Date | null {
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? null : d;
    }

    // UGLY parse to avoid timezone shift :(
    // e.g. in input type="date", I'd set 2025-09-23, it would reload as 2025-09-22
    public static parseLocalYmd(raw: string): Date | null {
        const m   = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
        if (!m) return null;
        const y   = parseInt(m[1], 10);
        const mo  = parseInt(m[2], 10);
        const d   = parseInt(m[3], 10);
        if (isNaN(y) || isNaN(mo) || isNaN(d)) return null;
        return new Date(y, mo - 1, d);
    }

    // Prefer strict, format-aware parsing; fall back to local YMD; then ISO; finally Date()
    public static parseDateWithFormat(raw: string, format?: string): Date | null {
        if (typeof raw !== 'string' || raw === '') return null;
        if (format) {
            const m = (moment as any)(raw, format, true);
            if (m.isValid()) return m.toDate();
        }
        const ymd = TypeParser.parseLocalYmd(raw);
        if (ymd) return ymd;
        const mISO = (moment as any)(raw, (moment as any).ISO_8601, true);
        if (mISO.isValid()) return mISO.toDate();
        return TypeParser.parseDate(raw);
    }

    public static formatDate(date: Date | null, format: string): string {
        return Number.isNaN(date?.getTime()) ? null : moment(date).format(format);
    }

    public static requireDate( value: Date | null ) : Date {
        const result = TypeParser.getDateOrDefault(value, null);
        if( result == null ) {
            throw new Error(`Invalid date value ${JSON.stringify(value)} submitted to TypeParser.requireDate`);
        }
        return result;
    }

    public static getDateOrDefault( value: Date | string | null | undefined, defaultValue : Date | string | null = null): Date | null {
        let result     = defaultValue ?? null;
        if( value instanceof Date ) {
            result     = value;
        }
        else if( value !== undefined && value !== null && value !== '' ) {
            result     = TypeParser.parseDate(value);
        }

        if( result instanceof Date ) {
            return result;
        }
        if( defaultValue instanceof Date ) {
            return defaultValue;
        }
        if( typeof defaultValue === 'string' ) {
            return TypeParser.parseDate(defaultValue);
        }
        
        return defaultValue;
    }

    public static getDateByFormatOrDefault(value: Date | string | null | undefined, format?: string, defaultValue: Date | null = null): Date | null {
        if (value instanceof Date) return value;
        if (typeof value === 'string' && value !== '') {
            return TypeParser.parseDateWithFormat(value, format) ?? defaultValue;
        }
        return defaultValue;
    }

    public static requireBool( value: string | boolean | number | null | undefined ) : boolean {
        const result = TypeParser.getBoolOrDefault(value, null);
        if( result == null ) {
            throw new Error(`Invalid boolean value ${JSON.stringify(value)} submitted to TypeParser.requireBool`);
        }
        return result;
    }

    public static getBoolOrDefault( value: string | boolean | number | null | undefined, defaultValue : boolean | null = null ): boolean | null {
        const stringValue   = value?.toString().toLowerCase() ?? '';
        if( TypeParser._trueOptions.includes(stringValue) ) {
            return true;
        }
        if( TypeParser._falseOptions.includes(stringValue) ) {
            return false;
        }
        return defaultValue;
    }

    public static requireInt( value: string | number ) : number {
        const result = TypeParser.getIntOrDefault(value);
        if( result == null ) {
            throw new Error(`Invalid integer value ${JSON.stringify(value)} submitted to TypeParser.requireInt`);
        }
        return result;
    }

    public static getIntOrDefault( value: string | number | null | undefined, defaultValue : number | null = null ): number | null {
        if( typeof value === "number" ) {
            return value;
        }
        if( typeof value === "string" ) {
            const parsed = parseInt(value, 10);
            if( !isNaN(parsed) ) {
                return parsed;
            }
        }
        return defaultValue;
    }

    public static requireFloat( value: string | number ) : number {
        const result = TypeParser.getFloatOrDefault(value);
        if( result == null ) {
            throw new Error(`Invalid float value ${JSON.stringify(value)} submitted to TypeParser.requireFloat`);
        }
        return result;
    }

    public static getFloatOrDefault( value: string | number | null | undefined, defaultValue : number | null = null ): number | null {
        if( typeof value === "number" ) {
            return value;
        }
        if( typeof value === "string" ) {
            const parsed = parseFloat(value);
            if( !isNaN(parsed) ) {
                return parsed;
            }
        }
        return defaultValue;
    }
}